import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAnalyticsAdoption } from '../../api/client';
import type {
  AdoptionAnalyticsResponse,
  AnalyticsGranularity,
  AnalyticsPeriod,
} from '../../api/types';
import { AnalyticsQueryState } from '../../analytics/AnalyticsQueryState';
import { PeriodGranularityControls } from '../../analytics/PeriodGranularityControls';
import { SimpleColumnChart } from '../../analytics/SimpleColumnChart';
import { parseGranularity, parsePeriod } from '../../analytics/analyticsParams';
import * as styles from './analyticsPageStyles';

function adoptionHasFunnelData(funnel: AdoptionAnalyticsResponse['adoptionFunnel']): boolean {
  if (!funnel) return false;
  const vals = [funnel.install, funnel.firstPaidAddition, funnel.retained].filter(
    (v) => v != null && v > 0
  );
  return vals.length > 0;
}

export function AdoptionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'));
  const granularity = parseGranularity(searchParams.get('granularity'));

  const setPeriod = useCallback(
    (p: AnalyticsPeriod) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('period', String(p));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setGranularity = useCallback(
    (g: AnalyticsGranularity) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('granularity', g);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [data, setData] = useState<AdoptionAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getAnalyticsAdoption({ period, granularity })
      .then(setData)
      .catch((e) => {
        setError(
          e instanceof Error ? e.message : 'Unable to load adoption data. Please try again.'
        );
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [period, granularity]);

  useEffect(() => {
    load();
  }, [load]);

  const series = data?.workspaceCountOverTime ?? [];
  const newInstalls = data?.newInstallsInPeriod ?? 0;
  const funnel = data?.adoptionFunnel;
  const isEmpty = series.length === 0 && newInstalls === 0 && !adoptionHasFunnelData(funnel);

  const chartData = series.map((p) => ({
    key: p.bucket,
    label: p.bucket.length > 10 ? p.bucket.slice(5) : p.bucket,
    value: p.cumulativeCount,
  }));

  return (
    <div data-testid="analytics-adoption-root">
      <PeriodGranularityControls
        period={period}
        onPeriodChange={setPeriod}
        showGranularity
        granularity={granularity}
        onGranularityChange={setGranularity}
      />

      <AnalyticsQueryState loading={loading} error={error} onRetry={load} isEmpty={isEmpty}>
        <div style={styles.card}>
          <p style={styles.sectionTitle}>Summary</p>
          <div style={styles.metricGrid} data-testid="analytics-adoption-summary">
            <div style={styles.metric}>
              <div style={styles.metricLabel}>New installs in period</div>
              <div style={styles.metricValue}>{newInstalls}</div>
            </div>
          </div>

          <p style={styles.sectionTitle}>Workspace count over time (cumulative)</p>
          {chartData.length > 0 && (
            <SimpleColumnChart data={chartData} data-testid="analytics-adoption-chart" />
          )}
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Bucket</th>
                  <th>Cumulative workspaces</th>
                </tr>
              </thead>
              <tbody>
                {series.map((row) => (
                  <tr key={row.bucket}>
                    <td>{row.bucket}</td>
                    <td>{row.cumulativeCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {adoptionHasFunnelData(funnel) && (
          <div style={styles.card}>
            <p style={styles.sectionTitle}>Adoption funnel</p>
            <div style={styles.metricGrid}>
              <div style={styles.metric}>
                <div style={styles.metricLabel}>Install</div>
                <div style={styles.metricValue}>{funnel?.install ?? '—'}</div>
              </div>
              <div style={styles.metric}>
                <div style={styles.metricLabel}>First paid addition</div>
                <div style={styles.metricValue}>{funnel?.firstPaidAddition ?? '—'}</div>
              </div>
              <div style={styles.metric}>
                <div style={styles.metricLabel}>Retained</div>
                <div style={styles.metricValue}>{funnel?.retained ?? '—'}</div>
              </div>
            </div>
          </div>
        )}
      </AnalyticsQueryState>
    </div>
  );
}
